import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { ProductUpdateInput } from '@ncc/shared';
import { ProductForm } from '../components/ProductForm';
import { api } from '../lib/api';

export async function editProductLoader({
  params,
  context,
}: {
  params: { id: string };
  context: { queryClient: QueryClient };
}) {
  return context.queryClient.ensureQueryData({
    queryKey: ['products', 'detail', Number(params.id)],
    queryFn: () => api.getProduct(Number(params.id)),
  });
}

export function EditProduct() {
  const { id } = useParams({ from: '/products/edit/$id' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: product } = useQuery({
    queryKey: ['products', 'detail', Number(id)],
    queryFn: () => api.getProduct(Number(id)),
  });

  const save = useMutation({
    mutationFn: async ({
      patch,
      images,
    }: {
      patch: ProductUpdateInput;
      images: { url: string; image_id?: string | null; position?: number }[];
    }) => {
      const updated = await api.updateProduct(Number(id), patch);
      const finalState = await api.replaceProductImages(Number(id), images);
      return finalState ?? updated;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['products', 'detail', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteProduct(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate({ to: '/products' });
    },
  });

  if (!product) return <div>Loading…</div>;

  return (
    <>
      <header className="toolbar">
        <h2 style={{ margin: 0 }}>
          <span className={`badge ${product.status}`} style={{ marginRight: 8 }}>
            {product.status.replace('_', ' ')}
          </span>
          {product.name}
        </h2>
        <span className="grow" />
        <button
          className="danger"
          onClick={() => {
            if (confirm(`Delete product "${product.name}"? This cannot be undone.`)) {
              remove.mutate();
            }
          }}
        >
          Delete
        </button>
        <Link to="/products">
          <button className="ghost">Back</button>
        </Link>
      </header>

      <div className="card">
        <ProductForm
          initial={product}
          submitLabel="Save changes"
          onSubmit={async (values, images) => {
            await save.mutateAsync({
              patch: values as ProductUpdateInput,
              images,
            });
          }}
        />
      </div>

      {save.isPending ? <div className="toast">Saving…</div> : null}
    </>
  );
}
